import React, { FC, useState } from "react";
import { styled } from "@mui/system";
import Dialog from "./Dialog";
import Input from "../DataInput/Input";
import Text, { TextVariant } from "./Text";
import { COLORS, SPACINGS } from "../../common/consts";

export type HubSpotFieldType = 'text' | 'select' | 'date';

interface CreateHubSpotPropertyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateProperty: (label: string, fieldType: HubSpotFieldType) => Promise<void>;
  fieldType: HubSpotFieldType;
  fieldLabel: string;
  isCreating: boolean;
}

const DialogContent = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.md};
  min-width: 320px;
  
  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const DescriptionText = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin-bottom: ${SPACINGS.sm};
`;

const FieldTypeLabel = styled("span")`
  font-weight: 500;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const CreateHubSpotPropertyDialog: FC<CreateHubSpotPropertyDialogProps> = ({
  open,
  onClose,
  onCreateProperty,
  fieldType,
  fieldLabel,
  isCreating,
}) => {
  const [propertyLabel, setPropertyLabel] = useState(fieldLabel);

  const handleCreate = async () => {
    if (!propertyLabel.trim()) return;
    await onCreateProperty(propertyLabel.trim(), fieldType);
  };

  const handleClose = () => {
    setPropertyLabel(fieldLabel);
    onClose();
  };

  // Reset label when dialog opens with a new field type
  React.useEffect(() => {
    if (open) {
      setPropertyLabel(fieldLabel);
    }
  }, [open, fieldLabel]);

  const getFieldTypeDescription = () => {
    switch (fieldType) {
      case 'select':
        return 'This will create a dropdown field with options: Valid, Invalid, and Questionable.';
      case 'date':
        return 'This will create a date field to store the verification timestamp.';
      case 'text':
        return 'This will create a text field to store the email source.';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Create HubSpot Property"
      maxWidth={440}
      primaryButtonProps={{
        children: isCreating ? "Creating..." : "Create Property",
        onClick: handleCreate,
        disabled: isCreating || !propertyLabel.trim(),
      }}
      secondaryButtonProps={{
        children: "Cancel",
        onClick: handleClose,
        disabled: isCreating,
      }}
    >
      <DialogContent>
        <DescriptionText variant={TextVariant.sm}>
          Create a new HubSpot contact property for <FieldTypeLabel>{fieldLabel}</FieldTypeLabel>.
        </DescriptionText>
        <Input
          label="Property Name"
          value={propertyLabel}
          onChange={(e) => setPropertyLabel(e.target.value)}
          placeholder="Enter property name"
          disabled={isCreating}
          autoFocus
        />
        <DescriptionText variant={TextVariant.sm} style={{ fontSize: '0.75rem' }}>
          {getFieldTypeDescription()}
        </DescriptionText>
      </DialogContent>
    </Dialog>
  );
};

export default CreateHubSpotPropertyDialog;
